import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import * as React from 'react';

import { describeError, formatTs } from './components';
import type { SandboxSuspensionState, Suspension } from './types';

/** "Suspend sandbox" (metaplan F2, SBX D6): the sandbox's kill switch. While suspended it imports
 *  nothing, sends nothing and refuses writes; reads keep working. An ops suspension is locked. */
const SuspensionPanel = ({
    suspension,
    canManage,
    onChange,
}: {
    suspension: Suspension;
    canManage: boolean;
    onChange: (suspended: boolean, reason?: string) => Promise<SandboxSuspensionState>;
}) => {
    const [confirming, setConfirming] = React.useState(false);
    const [reason, setReason] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);

    const apply = async (suspended: boolean) => {
        setConfirming(false);
        setBusy(true);
        setError(null);
        try {
            await onChange(suspended, suspended ? reason.trim().slice(0, 200) || undefined : undefined);
            setReason('');
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box data-testid="suspension-panel">
            <FormControlLabel
                control={(
                    <Switch
                        checked={suspension.suspended}
                        disabled={!canManage || suspension.locked || busy}
                        onChange={(ev) => (ev.target.checked ? setConfirming(true) : apply(false))}
                        inputProps={{ 'aria-label': 'Suspend sandbox' }}
                    />
                )}
                label="Suspend sandbox"
            />
            <Typography variant="body2" color="text.secondary">
                A suspended sandbox imports nothing, sends nothing and refuses writes; you can still read everything here.
            </Typography>
            {suspension.suspended && (
                <Alert severity={suspension.locked ? 'error' : 'warning'} sx={{ mt: 1 }}>
                    {suspension.locked
                        ? 'Curated Planet suspended this sandbox; only they can resume it.'
                        : 'The sandbox is suspended.'}
                    {suspension.at ? ` Since ${formatTs(suspension.at)}.` : ''}
                    {suspension.reason ? ` Reason: ${suspension.reason}` : ''}
                </Alert>
            )}
            {error ? <Alert severity="error" sx={{ mt: 1 }}>{describeError(error)}</Alert> : null}
            <Dialog open={confirming} onClose={() => setConfirming(false)} aria-labelledby="suspend-title">
                <DialogTitle id="suspend-title">Suspend the sandbox?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Imports, sends and writes stop until you resume it. Requests sent to it meanwhile are refused (423).
                    </DialogContentText>
                    <TextField
                        label="Reason (optional)"
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        value={reason}
                        onChange={(ev) => setReason(ev.target.value)}
                        inputProps={{ maxLength: 200 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={() => apply(true)}>Suspend</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SuspensionPanel;
